import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking } from '../../api/rooms';
import {
  dayNumberLabel,
  monthGridDays,
  timeLabel,
  todayInBuilding,
  weekdayLabel,
} from '../../lib/calendar';

/** How many reservations a cell shows before collapsing into "+n more". */
const VISIBLE_PER_DAY = 3;

export interface MonthGridProps {
  month: string;
  bookings: Booking[];
  onOpen: (booking: Booking) => void;
  onOpenDay: (date: string) => void;
  /** Clicking an empty part of a cell books that day. */
  onBookDay: (date: string) => void;
}

/**
 * The month view.
 *
 * Up to three reservations per cell, then a `+n more` control that drills into
 * that day (REQ-107). The FRS is specific that the overflow "opens that day
 * rather than truncating silently" — a cell that quietly drops the fourth
 * meeting is worse than one that admits it.
 */
export function MonthGrid({
  month,
  bookings,
  onOpen,
  onOpenDay,
  onBookDay,
}: MonthGridProps): ReactElement {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const today = todayInBuilding();

  const days = useMemo(() => monthGridDays(month), [month]);

  const byDate = useMemo(() => {
    const map = new Map<string, Booking[]>();
    for (const booking of bookings) {
      const list = map.get(booking.date) ?? [];
      list.push(booking);
      map.set(booking.date, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    return map;
  }, [bookings]);

  const currentMonth = month.slice(0, 7);

  return (
    <div className="cal-wrap cal-month">
      <div className="cm-head">
        {days.slice(0, 7).map((day) => (
          <div key={day}>{weekdayLabel(day, locale)}</div>
        ))}
      </div>

      <div className="cm-grid">
        {days.map((day) => {
          const list = byDate.get(day) ?? [];
          const shown = list.slice(0, VISIBLE_PER_DAY);
          const hidden = list.length - shown.length;
          const outside = day.slice(0, 7) !== currentMonth;

          /*
           * BR-017 — a past day cannot be booked, so it does not offer to be.
           * Letting the click through would open a form the server is certain to
           * refuse, which is a worse answer than not offering it.
           */
          const isBookable = day >= today;

          return (
            <div
              key={day}
              className={[
                'cm-cell',
                outside ? 'out' : '',
                day === today ? 'today' : '',
                isBookable ? 'bookable' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              /*
               * The cell itself is not focusable — the day number inside it
               * already is, and it does the same thing. Two tab stops per cell
               * would mean 42 of them before the grid is crossed.
               */
              onClick={(event) => {
                if (!isBookable) return;
                // A click on a reservation, the day number or "+n more" belongs
                // to that control.
                if ((event.target as HTMLElement).closest('button')) return;
                onBookDay(day);
              }}
            >
              <button
                type="button"
                className="cm-day"
                onClick={() => onOpenDay(day)}
                aria-label={t('cal_day') + ' ' + day}
              >
                {dayNumberLabel(day, locale)}
              </button>

              <div className="cm-evs">
                {shown.map((booking) => (
                  <button
                    key={booking.id}
                    type="button"
                    className="cm-ev"
                    title={booking.title}
                    onClick={() => onOpen(booking)}
                  >
                    {timeLabel(booking.starts_at, locale)} · {booking.title}
                  </button>
                ))}

                {hidden > 0 ? (
                  <button type="button" className="cm-more" onClick={() => onOpenDay(day)}>
                    +{hidden} {t('cal_more')}
                  </button>
                ) : null}

                {/*
                  The keyboard path to the same action the cell click gives a
                  mouse. Visible only on hover or focus, so the grid reads as the
                  prototype's while still being operable without a pointer.
                */}
                {isBookable ? (
                  <button
                    type="button"
                    className="cm-book"
                    onClick={() => onBookDay(day)}
                    aria-label={`${t('bk_new')} — ${day}`}
                  >
                    +
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
